import { ENGINE_BAY_AMPACITY_DERATING } from "./cable-current";
import { requireEvidence, requireNumber, type CalculatorIssues } from "./calculator-input";
import { AWG_WIRE_TABLE, CONTINUOUS_LOAD_HEADROOM, CU_RHO, MIN_GENERAL_CIRCUIT_MM2, SELECTABLE_WIRE_TABLE, selectWire, voltageDrop, type WireSpec } from "./wire-tables";
import type { UnitSystem } from "./units/units";

export type WireSizeInput = {
  voltageV: number | null;
  currentA: number | null;
  /** Physical positive run; an equal-length, same-size copper return is assumed. */
  oneWayLengthM: number | null;
  dropPct: 3 | 10 | null;
  insulation: "105" | "other" | null;
  environment: "outside-30" | "engine-50" | "other" | null;
  routing: "two-conductors" | "bundle" | "other" | null;
  /** Additional ampacity multiplier, after the engine-space multiplier. */
  bundleFactor: number | null;
  bundleEvidence: string;
};

export type WireSizeResult = CalculatorIssues & {
  status: "unknown" | "invalid" | "exceeds-table" | "ready";
  wire: WireSpec | null;
  ampacityWire: WireSpec | null;
  dropWire: WireSpec | null;
  requiredAmpacityA: number | null;
  requiredAreaMm2: number | null;
  derating: number | null;
  installedAmpacityA: number | null;
  dropV: number | null;
  dropPct: number | null;
  limiting: "ampacity" | "voltage-drop" | "both" | "mechanical-minimum" | null;
};

export function calculateWireSize(input: WireSizeInput, units: UnitSystem = "metric"): WireSizeResult {
  const result: WireSizeResult = { status: "unknown", missing: [], invalid: [], unsupported: [], wire: null,
    ampacityWire: null, dropWire: null, requiredAmpacityA: null, requiredAreaMm2: null,
    derating: null, installedAmpacityA: null, dropV: null, dropPct: null, limiting: null };
  requireNumber(result, "Circuit voltage (V)", input.voltageV, 1, 60);
  requireNumber(result, "Continuous current (A)", input.currentA, 0.01, 10000);
  requireNumber(result, "One-way length (m)", input.oneWayLengthM, 0.01, 1000);
  if (input.dropPct == null) result.missing.push("Voltage-drop allowance");
  else if (input.dropPct !== 3 && input.dropPct !== 10) result.invalid.push("Choose a 3% or 10% circuit allowance.");
  for (const [label, value, supported] of [
    ["Insulation rating", input.insulation, ["105"]],
    ["Temperature / engine space", input.environment, ["outside-30", "engine-50"]],
    ["Conductor routing", input.routing, ["two-conductors", "bundle"]],
  ] as const) {
    if (value == null) result.missing.push(label);
    else if (!(supported as readonly string[]).includes(value)) result.unsupported.push(label);
  }
  if (input.routing === "bundle") {
    requireNumber(result, "Documented bundle factor", input.bundleFactor, 0.01, 1);
    requireEvidence(result, "Bundle factor source", input.bundleEvidence);
  }
  if (result.invalid.length) result.status = "invalid";
  if (result.invalid.length || result.missing.length || result.unsupported.length) return result;
  const current = input.currentA!;
  const length = input.oneWayLengthM!;
  const voltage = input.voltageV!;
  const derating = (input.environment === "engine-50" ? ENGINE_BAY_AMPACITY_DERATING : 1) *
    (input.routing === "bundle" ? input.bundleFactor! : 1);
  const requiredAmpacityA = current * CONTINUOUS_LOAD_HEADROOM;
  const requiredAreaMm2 = 2 * current * length * CU_RHO / (voltage * input.dropPct! / 100);
  const table = units === "imperial" ? AWG_WIRE_TABLE : SELECTABLE_WIRE_TABLE;
  const ampacityWire = table.find(w => w.ampacity * derating >= requiredAmpacityA) ?? null;
  const dropWire = table.find(w => w.mm2 >= requiredAreaMm2) ?? null;
  Object.assign(result, { requiredAmpacityA, requiredAreaMm2, ampacityWire, dropWire, derating });
  // selectWire intentionally clamps for legacy editor callers. A public
  // calculator must not present that fallback as a satisfying conductor.
  if (!ampacityWire || !dropWire) return { ...result, status: "exceeds-table" };
  const wire = selectWire(current, length, voltage, input.dropPct!, CONTINUOUS_LOAD_HEADROOM, 0, derating, MIN_GENERAL_CIRCUIT_MM2, table);
  const dropV = voltageDrop(current, length, wire.mm2);
  const limiting = ampacityWire.mm2 === table[0].mm2 && dropWire.mm2 === table[0].mm2
    ? "mechanical-minimum" : ampacityWire.mm2 === dropWire.mm2 ? "both"
    : ampacityWire.mm2 > dropWire.mm2 ? "ampacity" : "voltage-drop";
  return { ...result, status: "ready", wire, limiting, dropV, dropPct: dropV / voltage * 100, installedAmpacityA: wire.ampacity * derating };
}

export const EMPTY_WIRE_INPUT: WireSizeInput = { voltageV: null, currentA: null, oneWayLengthM: null, dropPct: null,
  insulation: null, environment: null, routing: null, bundleFactor: null, bundleEvidence: "" };

export const EXAMPLE_WIRE_INPUT: WireSizeInput = { ...EMPTY_WIRE_INPUT, voltageV: 12, currentA: 20, oneWayLengthM: 5,
  dropPct: 3, insulation: "105", environment: "outside-30", routing: "two-conductors" };
